import React, { useState, useEffect, useRef } from 'react';
import { 
    Pencil, Plus, Minus, Mail, Phone, Building, User, MapPin, 
    Globe, Landmark, Activity, Target, Layers, Tag as TagIcon, X, ArrowLeft,
    MessageSquare, MessageCircle, StickyNote, CheckSquare, Calendar, DollarSign,
    Upload as UploadIcon, List as ListIcon, Mic2, Banknote, FileText
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import './EntityEdit.css';
import Loader from '../../Loader/Loader.jsx';
import API_ENDPOINTS, { buildUrl } from '../../../config/apiEndpoints.js';
import { getSession } from '../../../getSession.js';
import LeadActionOperation from '../../../common/LeadActionOperation/LeadActionOperation';
import Alert from '../../../common/Alerts/Alert';

const WhatsAppIcon = ({ size = 20 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L0 24l6.335-1.662c1.72.94 3.659 1.437 5.634 1.437h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" fill="currentColor"/>
    </svg>
);

// Reusing constants from Enquiry LeadDetails
const MERGE_FIELDS = [
    { label: 'Name', key: 'PersonName', required: true, icon: <User size={16} /> },
    { label: 'Company', key: 'CompanyName', icon: <Building size={16} /> },
    { label: 'Mobile', key: 'MobileNo', type: 'tel', icon: <Phone size={16} /> },
    { label: 'Email', key: 'EmailId', type: 'email', icon: <Mail size={16} /> },
    { label: 'Address', key: 'Address', type: 'textarea', icon: <MapPin size={16} /> }
];

const HARDCODED_OTHER_DETAILS = [];

const countryList = [
    { name: "India", dialCode: "+91", flag: "https://flagcdn.com/w320/in.png", code: "IN" }
];
const NEW_ADDED_BASE_URL = (process.env.REACT_APP_SERVICES_API_BASE_URL || '').replace(/\/$/, '');
const Get_Custom_Fields_New = `${NEW_ADDED_BASE_URL}${API_ENDPOINTS.COMMON.GET_CUSTOM_FIELDS_NEW}`;
const Get_Custom_Fields_Mapping_New = `${NEW_ADDED_BASE_URL}${API_ENDPOINTS.COMMON.GET_LEAD_DETAILS_WITH_CUSTOM}`;

  const API_BASE = (process.env.REACT_APP_WCF_API_BASE_URL || '').replace(/\/$/, '');
const GetLeadDetailByLeadId = `${NEW_ADDED_BASE_URL}${API_ENDPOINTS.PIPELINE.GET_SCORECARD_DETAILS_NEW}`;
const NEWV3_BASE_URL = (process.env.REACT_APP_SERVICES_API_BASE_URL || '').replace(/\/$/, '');
const Get_campaign_for_edit = `${NEWV3_BASE_URL}${API_ENDPOINTS.FOLLOWUPS.GET_CAMPAIGN_FOR_EDIT}`;
const Course = `${NEWV3_BASE_URL}${API_ENDPOINTS.FOLLOWUPS.GET_CENTER_OF_EDUCATION_EDIT}`;
const SaveEdit = `${NEWV3_BASE_URL}/UserCRM/EditLead`;

const EntityEdit = ({ entityData = {}, onSave, onCancel, isLoading: parentLoading = false }) => {
    const navigate = useNavigate();
    const { TokenId: TOKEN, userId: LOGGED_USER_ID, parentId: PARENT_ID } = getSession();

    // Form State
    const [formData, setFormData] = useState({
        PersonName: '',
        CompanyName: '',
        MobileNo: [''],
        EmailId: [''],
        CountryId: '',
        StateId: '',
        CityId: '',
        MediumId: '',
        CampaignId: '',
        SourceId: '',
        ResidentialAddress: '',
        OfficialAddress: '',
        InitialRemarks: '',
        Pincode: '',
        Tags: '',
        // Additional Fields
        AssignedTo: 0,
        FollowUpStatus: 0,
        AmountPaid: 0,
        CreatedBy: 0,
        ModifiedBy: 0,
        NextStatusDate: '',
        Remarks: '',
        Products: '',
        FollowupDate: '',
        WhereAddress: '',
        WhereLatitude: '',
        WhereLongitude: '',
        CreatedOn: '',
        ModifiedOn: '',
        LeadNo: '',
        // Dynamic Fields
        ...HARDCODED_OTHER_DETAILS.reduce((acc, curr) => {
            const cleanKey = curr.Text.replace(/\|/g, '').replace(/v$/, '');
            acc[cleanKey] = '';
            return acc;
        }, {})
    });

    // Dropdown States
    const [dropdowns, setDropdowns] = useState({
        countries: [],
        states: [],
        cities: [],
        mediums: [],
        campaigns: [],
        sources: []
    });

    const [isLoading, setIsLoading] = useState(false);
    const [isDetailsLoading, setIsDetailsLoading] = useState(false);
    const [customFields, setCustomFields] = useState([]);

    const DEFAULT_COUNTRY = { 
        name: "India", 
        dialCode: "+91", 
        flag: "https://flagcdn.com/w20/in.png", 
        code: "IN" 
    };
    const [allCountries, setAllCountries] = useState([]);
    const [selectedCountries, setSelectedCountries] = useState([null, null, null]);
    const [activeCountryPickerIdx, setActiveCountryPickerIdx] = useState(null);
    const [tagInputValue, setTagInputValue] = useState('');
    const [countrySearchTerm, setCountrySearchTerm] = useState('');
    const [courseOptions, setCourseOptions] = useState([]);
    const [avatarPreview, setAvatarPreview] = useState(null);
    const [activeAction, setActiveAction] = useState(null);

    // Alert State
    const [alertConfig, setAlertConfig] = useState({
        show: false,
        type: 'success',
        title: 'Success',
        message: '',
        onConfirm: null
    });
    const countryPickerRef = React.useRef(null);
    const fileInputRef = React.useRef(null);

    const SIDEBAR_ACTIONS = [
        { id: 'followup', icon: <Calendar size={20} />, label: 'Follow-up', color: '#6366f1' },
        { id: 'sms', icon: <MessageSquare size={20} />, label: 'SMS', color: '#f59e0b' },
        { id: 'voice', icon: <Mic2 size={20} />, label: 'Voice', color: '#ef4444' },
        { id: 'mail', icon: <Mail size={20} />, label: 'Email', color: '#2563eb' },
        { id: 'whatsapp', icon: <WhatsAppIcon size={20} />, label: 'WhatsApp', color: '#0ea5e9' },
        { id: 'notes', icon: <StickyNote size={20} />, label: 'Notes', color: '#d97706' },
        { id: 'upload-document', icon: <UploadIcon size={20} />, label: 'Collateral', color: '#f97316' },
        { id: 'add-task', icon: <CheckSquare size={20} />, label: 'Task', color: '#10b981' },
        { id: 'add-appointment', icon: <Calendar size={20} />, label: 'Appointment', color: '#8b5cf6' },
        { id: 'add-deal', icon: <DollarSign size={20} />, label: 'Deal', color: '#22c55e' },
        { id: 'fill-webform', icon: <Globe size={20} />, label: 'Webform', color: '#0284c7' },
        { id: 'tax-setting', icon: <Banknote size={20} />, label: 'Tax Setting', color: '#f43f5e' },
        { id: 'quotation', icon: <MessageSquare size={20} />, label: 'Quotation', color: '#a855f7' },
        { id: 'invoice', icon: <FileText size={20} />, label: 'Invoice', color: '#14b8a6' },
    ];

    useEffect(() => {
        fetchAllCountries();
        fetchCourseOptions();
    }, []);

    const fetchAllCountries = async () => {
        try {
            const response = await fetch('https://raw.githubusercontent.com/mledoze/countries/master/countries.json');
            const data = await response.json();
            const mapped = data.map(c => {
                const root = c.idd?.root || '';
                const suffix = c.idd?.suffixes?.[0] || '';
                return {
                    name: c.name.common,
                    flag: c.flags?.png || `https://flagcdn.com/w320/${(c.cca2 || '').toLowerCase()}.png`,
                    dialCode: root + suffix,
                    code: c.cca2
                };
            }).filter(c => c.dialCode)
              .sort((a, b) => a.name.localeCompare(b.name));

            setAllCountries(mapped);
            const india = mapped.find(c => c.code === 'IN');
            if (india) setSelectedCountries([india, india, india]);
        } catch (error) {
            console.error('Error fetching restcountries:', error);
        }
    };

    const fetchCourseOptions = async () => {
        try {
            const leadNoVal = entityData?.LeadNo || entityData?.leadno || 42957;
            const payload = {
                Token: "-2295521862261168",
                Details: {
                    leadno: parseInt(leadNoVal),
                    parentid: parseInt(PARENT_ID || 34594)
                }
            };
            const response = await fetch(Course, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const data = await response.json();
            
            const rawChoices = data.Details?.[0] || data.d?.[0] || data.Details || data.d || "";
            if (rawChoices && typeof rawChoices === 'object' && rawChoices.FollowupStatus) {
                // If it returned an object, perhaps mapping isn't directly 'Choices'
                // The new endpoint might return FollowupStatus, Remarks, etc. We'll handle it if needed.
            } else if (rawChoices && rawChoices.Choices) {
                const choicesArray = rawChoices.Choices.split(',').map(c => c.trim()).filter(c => c);
                setCourseOptions(choicesArray);
            } else {
                // Fallback to user provided string if API fails or returns empty
                const fallback = "Diploma in Computers,Diploma in Mechanical,Diploma in Pharma,BSC,BCA,BBA,BCOM,BA,BALLB,Bed,MSC,MCA,MBA,MCOM,MA,Med,PHD";
                setCourseOptions(fallback.split(',').map(c => c.trim()));
            }
        } catch (error) {
            console.error('Error fetching course options:', error);
            const fallback = "Diploma in Computers,Diploma in Mechanical,Diploma in Pharma,BSC,BCA,BBA,BCOM,BA,BALLB,Bed,MSC,MCA,MBA,MCOM,MA,Med,PHD";
            setCourseOptions(fallback.split(',').map(c => c.trim()));
        }
    };

    // Handle Click Outside for Dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (countryPickerRef.current && !countryPickerRef.current.contains(event.target)) {
                setActiveCountryPickerIdx(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Initial Data Load
    useEffect(() => {
        fetchInitialDropdowns();
        const leadId = entityData?.LeadId || entityData?.leadId || entityData?.enquiryId || entityData?.id;
        
        // Immediate mapping of basic fields from the list view data
        const initialBasicData = {
            PersonName: entityData?.PersonName || entityData?.personname || entityData?.name || entityData?.Name || '',
            MobileNo: (entityData?.Mobile || entityData?.mobileno || entityData?.phone || entityData?.Phone || '').split(',').map(m => m.trim()).filter(m => m) || [''],
            EmailId: (entityData?.Email || entityData?.emailid || entityData?.email || '').split(',').map(e => e.trim()).filter(e => e) || [''],
            LeadId: leadId || '',
            Company: entityData?.CompanyName || entityData?.companyname || entityData?.company || '',
            City: entityData?.City || entityData?.city || '',
            StateId: entityData?.StateId || entityData?.stateid || '',
            CountryId: entityData?.CountryId || entityData?.countryid || '',
            AssignedTo: entityData?.AssignedTo || entityData?.assigned_to || 0,
            FollowUpStatus: entityData?.FollowUpStatus || 0,
            AmountPaid: entityData?.AmountPaid || 0,
            CreatedBy: entityData?.CreatedBy || 0,
            ModifiedBy: entityData?.ModifiedBy || 0,
            NextStatusDate: entityData?.NextStatusDate || '',
            Remarks: entityData?.Remarks || '',
            Products: entityData?.Products || '',
            FollowupDate: entityData?.FollowupDate || '',
            CreatedOn: entityData?.CreatedOn || '',
            ModifiedOn: entityData?.ModifiedOn || '',
            LeadNo: entityData?.LeadNo || entityData?.leadno || ''
        };

        if (initialBasicData.MobileNo.length === 0) initialBasicData.MobileNo = [''];
        if (initialBasicData.EmailId.length === 0) initialBasicData.EmailId = [''];

        if (leadId) {
            setFormData(prev => ({ ...prev, ...initialBasicData }));
            fetchEntityDetails(leadId);
            fetchDynamicCustomFields(leadId);
        } else if (entityData) {
            setFormData(prev => ({ ...prev, ...initialBasicData, ...entityData }));
        }
    }, [entityData?.LeadId, entityData?.leadId, entityData?.enquiryId, entityData?.id]);

    const fetchDynamicCustomFields = async (id) => {
        try {
            const token = TOKEN || localStorage.getItem('TokenId') || "-2295521862261168";
            const userId = LOGGED_USER_ID || localStorage.getItem('userId') || "34594";
            const parentId = PARENT_ID || localStorage.getItem('parentId') || userId;

            // 1. Fetch Custom Field Definitions
            const defRes = await fetch(Get_Custom_Fields_New, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    Token: token,
                    Message: "",
                    LoggedUserId: parseInt(userId, 10),
                    MAC_Address: "",
                    IP_Address: "",
                    Details: { ParentId: parseInt(parentId, 10) }
                })
            });
            const defData = await defRes.json();
            const fields = defData.Details || (defData.d && defData.d.Details) || [];
            setCustomFields(fields);

            // Initialize formData with dynamic keys if not already there
            setFormData(prev => {
                const next = { ...prev };
                fields.forEach(f => {
                    if (!(f.FieldName in next)) {
                        next[f.FieldName] = "";
                    }
                });
                return next;
            });

            // 2. Fetch Mapping Values for this Lead
            const mapRes = await fetch(Get_Custom_Fields_Mapping_New, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    Token: token,
                    Details: JSON.stringify({
                        draw: 1,
                        StartNo: 0,
                        EndNo: 1,
                        UserId: parseInt(userId, 10),
                        ParentId: parseInt(parentId, 10),
                        FilterText: "ID=" + id
                    })
                })
            });
            const mapData = await mapRes.json();

            let mappingRow = null;
            const mappingDetails = mapData.Details || (mapData.d && mapData.d.Details) || mapData.d;
            if (mappingDetails) {
                const parsedDetails = typeof mappingDetails === 'string' ? JSON.parse(mappingDetails) : mappingDetails;
                mappingRow = (Array.isArray(parsedDetails) ? parsedDetails[0] : null)
                    || (parsedDetails && !Array.isArray(parsedDetails) ? (parsedDetails.Table?.[0] || parsedDetails.Data?.[0] || parsedDetails) : null);
            }

            if (mappingRow) {
                setFormData(prev => {
                    const next = { ...prev };
                    fields.forEach(f => {
                        const val = mappingRow[f.FieldName] || mappingRow[f.FieldName.toLowerCase()] || mappingRow[f.FieldName.replace(/\s/g, '_')] || "";
                        next[f.FieldName] = val;
                    });
                    return next;
                });
            }
        } catch (e) {
            console.error("Error fetching dynamic custom fields:", e);
        }
    };

    const fetchEntityDetails = async (leadId) => {
        setIsDetailsLoading(true);
        try {
            // Use SERVICES_AZURE for direct hits or stay with WCF if it's the confirmed server. 
            // The image showed WCF + /UserCRM/ failing, so we try the direct STAGE1/SERVICES base if available.
            const url = buildUrl('SERVICES_AZURE', API_ENDPOINTS.PIPELINE.GET_SCORECARD_DETAILS_NEW);
            const payload = { 
                Token: TOKEN || "-2295521862261168", 
                Message: "", 
                LoggedUserId: parseInt(LOGGED_USER_ID, 10), 
                MAC_Address: "", 
                IP_Address: "",
                Details: { 
                    LeadId: parseInt(leadId, 10) 
                } 
            };
            
            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const data = await response.json();
            
            if (data?.Details) {
                const inner = typeof data.Details === 'string' ? JSON.parse(data.Details) : data.Details;
                const raw = Array.isArray(inner) ? inner[0] : (inner || {});
                
                // Map API response to our form state keys based on provided sample
                const mappedData = {
                    PersonName: raw.personname || '',
                    CompanyName: raw.companyname || '',
                    MobileNo: (raw.mobileno || '').split(',').map(m => m.trim()).filter(m => m),
                    EmailId: (raw.emailid || '').split(',').map(e => e.trim()).filter(e => e),
                    CountryId: raw.CountryID || raw.country_id || raw.CountryId || raw.countryid || '',
                    StateId: raw.StateID || raw.state_id || raw.StateId || raw.stateid || '',
                    CityId: raw.CityID || raw.city_id || raw.CityId || raw.cityid || '',
                    SourceId: raw.source_id || '',
                    MediumId: raw.medium_id || '',
                    CampaignId: raw.camp_id || '',
                    ResidentialAddress: raw.residentialaddress || raw.Address || '',
                    OfficialAddress: raw.officeaddress || '',
                    InitialRemarks: raw.remark || '',
                    Pincode: raw.pincode || '',
                    Tags: raw.groups || '',
                    LeadNo: raw.leadno || '',
                    AssignedTo: raw.AssignedTo || raw.assigned_to || 0,
                    FollowUpStatus: raw.FollowUpStatus || 0,
                    AmountPaid: raw.AmountPaid || 0,
                    CreatedBy: raw.CreatedBy || raw.created_by || 0,
                    ModifiedBy: raw.ModifiedBy || raw.modified_by || 0,
                    NextStatusDate: raw.NextStatusDate || '',
                    Remarks: raw.Remarks || '',
                    Products: raw.Products || '',
                    FollowupDate: raw.FollowupDate || '',
                    WhereAddress: raw.WhereAddress || '',
                    WhereLatitude: raw.WhereLatitude || '',
                    WhereLongitude: raw.WhereLongitude || '',
                    CreatedOn: raw.CreatedOn || '',
                    ModifiedOn: raw.ModifiedOn || ''
                };

                if (mappedData.MobileNo.length === 0) mappedData.MobileNo = [''];
                if (mappedData.EmailId.length === 0) mappedData.EmailId = [''];

                // Map Dynamic Custom Fields
                HARDCODED_OTHER_DETAILS.forEach(detail => {
                    const cleanKey = detail.Text.replace(/\|/g, '').replace(/v$/, '');
                    // API might return lowercase version or exact key
                    mappedData[cleanKey] = raw[cleanKey] || raw[cleanKey.toLowerCase()] || raw[detail.Text] || '';
                });

                setFormData(prev => ({ ...prev, ...mappedData }));

                // Load dependent dropdowns
                if (mappedData.CountryId) handleCountryChange(mappedData.CountryId, true, mappedData.StateId);
            }
        } catch (error) {
            console.error('Error fetching entity details:', error);
        } finally {
            setIsDetailsLoading(false);
        }
    };

    const fetchInitialDropdowns = async () => {
        setIsLoading(true);
        try {
            const uid = LOGGED_USER_ID || "34594";
            const [countries, mediums, sources, campaigns] = await Promise.all([
                fetchDropdownData(`${NEW_ADDED_BASE_URL}/Common/GetCountryList`, { Token: "-2295521862261168", Details: "" }),
                fetchDropdownData(Get_campaign_for_edit, { Token: "-2295521862261168", Details: { Type: "MediumName", Parentid: parseInt(uid, 10) } }),
                fetchDropdownData(Get_campaign_for_edit, { Token: "-2295521862261168", Details: { Type: "SourceName", Parentid: parseInt(uid, 10) } }),
                fetchDropdownData(Get_campaign_for_edit, { Token: "-2295521862261168", Details: { Type: "CampaignName", Parentid: parseInt(uid, 10) } })
            ]);

            const ensureArray = (data) => {
                if (!data) return [];
                const res = data.d || data.Details || data;
                if (typeof res === 'string') {
                    try { return JSON.parse(res); } catch (e) { return []; }
                }
                return Array.isArray(res) ? res : [];
            };

            setDropdowns(prev => ({
                ...prev,
                countries: ensureArray(countries),
                mediums: ensureArray(mediums),
                sources: ensureArray(sources),
                campaigns: ensureArray(campaigns)
            }));

            if (entityData.CountryId || entityData.countryid) {
                handleCountryChange(entityData.CountryId || entityData.countryid, true);
            }
        } catch (error) {
            console.error('Error fetching dropdowns:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchDropdownData = async (url, payload) => {
        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            return await response.json();
        } catch (e) { return []; }
    };

    const handleCountryChange = async (countryId, isInitial = false, savedStateId = null) => {
        if (!isInitial) setFormData(prev => ({ ...prev, CountryId: countryId, StateId: '', CityId: '' }));
        // Ensure we only pass CountryId if it's not empty
        if (!countryId) {
            setDropdowns(prev => ({ ...prev, states: [], cities: [] }));
            return;
        }
        const states = await fetchDropdownData(buildUrl('WCF', API_ENDPOINTS.INVOICE.GET_STATE_BY_COUNTRY), { CountryId: countryId });
        setDropdowns(prev => ({ ...prev, states: states?.d || states || [], cities: [] }));
        
        const stateToLoad = savedStateId || (isInitial && formData.StateId);
        if (stateToLoad) {
            handleStateChange(stateToLoad, true);
        }
    };

    const handleStateChange = async (stateId, isInitial = false) => {
        if (!isInitial) setFormData(prev => ({ ...prev, StateId: stateId, CityId: '' }));
        if (!stateId) {
            setDropdowns(prev => ({ ...prev, cities: [] }));
            return;
        }
        const cities = await fetchDropdownData(buildUrl('WCF', API_ENDPOINTS.INVOICE.GET_CITY_BY_STATE), { StateId: stateId });
        setDropdowns(prev => ({ ...prev, cities: cities?.d || cities || [] }));
    };

    // Removed fetchCampaigns as it is now part of initial load or handled differently

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        const val = type === 'checkbox' ? checked : value;
        setFormData(prev => ({ ...prev, [name]: val }));
    };

    const handleMultiInputChange = (name, index, value) => {
        setFormData(prev => {
            const arr = [...(prev[name] || [''])];
            let val = value;
            if (name === 'MobileNo') {
                val = value.replace(/\D/g, '');
            }
            arr[index] = val;
            return { ...prev, [name]: arr };
        });
    };

    const addMultiField = (name) => {
        setFormData(prev => {
            const arr = [...(prev[name] || [''])];
            if (arr.length < 3) {
                return { ...prev, [name]: [...arr, ''] };
            }
            return prev;
        });
    };

    const removeMultiField = (name, index) => {
        setFormData(prev => {
            const arr = [...(prev[name] || [''])];
            if (arr.length > 1) {
                const newArr = arr.filter((_, i) => i !== index);
                return { ...prev, [name]: newArr };
            }
            return prev;
        });
    };

    const getFieldType = (cleanKey, dataType) => {
        const lower = cleanKey.toLowerCase();
        const lowerDataType = dataType ? dataType.toLowerCase() : '';

        if (lowerDataType === 'checkbox') return 'checkbox';
        if (lowerDataType === 'numeric') return 'number';
        
        if (lower.includes('studylevel') || lower.includes('centre_for_education') || lower.includes('course')) return 'select';
        if (lower.includes('date') || lower.includes('dob') || lower.includes('time') || lower.includes('till')) return 'date';
        if (lower === 'gender' || lower === 'is_adult' || lower === 'sex' || lower === 'active') return 'radio';
        return 'text';
    };

    const getSelectOptions = (cleanKey) => {
        const lower = cleanKey.toLowerCase();
        if (lower.includes('studylevel')) return ["Diploma", "Graduate", "Post Graduate"];
        if (lower.includes('centre_for_education')) return ["Delhi", "Jaipur", "Kota", "Lucknow"];
        if (lower.includes('course')) return courseOptions;
        return [];
    };

    const getRadioOptions = (cleanKey) => {
        const lower = cleanKey.toLowerCase();
        if (lower === 'gender' || lower === 'sex') return ['Male', 'Female', 'Other'];
        if (lower === 'is_adult' || lower === 'active') return ['Yes', 'No'];
        return ['Yes', 'No'];
    };

    const formatDateForInput = (val) => {
        if (!val) return '';
        // If it's already YYYY-MM-DD
        if (/^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
        
        const date = new Date(val);
        if (!isNaN(date.getTime())) {
            return date.toISOString().split('T')[0];
        }
        return val;
    };

    const handleTagKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === 'Tab' || e.key === ',') {
            e.preventDefault();
            const tag = tagInputValue.trim();
            const currentTags = formData.Tags ? formData.Tags.split(',').map(t => t.trim()).filter(t => t) : [];
            if (tag && !currentTags.includes(tag)) {
                const updatedTags = [...currentTags, tag].join(', ');
                setFormData(prev => ({ ...prev, Tags: updatedTags }));
            }
            setTagInputValue('');
        }
    };

    const handleTagPaste = (e) => {
        e.preventDefault();
        const paste = e.clipboardData.getData('text');
        const pastedTags = paste.split(/[,|\n]+/).map(t => t.trim()).filter(t => t);
        const currentTags = formData.Tags ? formData.Tags.split(',').map(t => t.trim()).filter(t => t) : [];
        const combined = [...new Set([...currentTags, ...pastedTags])];
        setFormData(prev => ({ ...prev, Tags: combined.join(', ') }));
    };

    const removeTag = (tagToRemove) => {
        const currentTags = formData.Tags ? formData.Tags.split(',').map(t => t.trim()).filter(t => t) : [];
        const updatedTags = currentTags.filter(t => t !== tagToRemove).join(', ');
        setFormData(prev => ({ ...prev, Tags: updatedTags }));
    };

    const handleAvatarClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 2 * 1024 * 1024) { // 2MB Limit
                alert("File size should be less than 2MB");
                return;
            }
            const reader = new FileReader();
            reader.onloadend = () => {
                setAvatarPreview(reader.result);
                setFormData(prev => ({ ...prev, AvatarFile: file }));
            };
            reader.readAsDataURL(file);
        }
    };

    const getInitials = (name) => {
        if (!name) return '93';
        const trimmed = name.trim();
        
        // Handle phone numbers (e.g., +91... or 93...)
        const cleanNumber = trimmed.startsWith('+') ? trimmed.slice(1) : trimmed;
        if (/^\d+$/.test(cleanNumber)) {
            return cleanNumber.slice(0, 2);
        }

        // Handle names (e.g., Shubham Mane -> SM)
        const parts = trimmed.split(/\s+/).filter(p => p.length > 0);
        if (parts.length >= 2) {
            return (parts[0][0] + parts[1][0]).toUpperCase();
        }
        return parts[0].slice(0, 2).toUpperCase();
    };

    const showAlert = (type, title, message, onConfirm = null) => {
        setAlertConfig({
            show: true,
            type,
            title,
            message,
            onConfirm
        });
    };

    const handleAlertClose = () => {
        const confirmCallback = alertConfig.onConfirm;
        setAlertConfig(prev => ({ ...prev, show: false }));
        if (confirmCallback) confirmCallback();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validation
        if (!formData.PersonName?.trim()) {
            showAlert('error', 'Required Field', 'Please enter a Name');
            return;
        }

        if (!formData.MobileNo || !formData.MobileNo[0] || !formData.MobileNo[0].trim()) {
            showAlert('error', 'Required Field', 'Please enter a Primary Mobile Number');
            return;
        }

        if (!formData.SourceId || formData.SourceId === "" || formData.SourceId === "0") {
            showAlert('error', 'Required Field', 'Please select a Source');
            return;
        }

        setIsLoading(true);

        try {
            let avatarBase64 = "";
            let avatarExt = "";
            let avatarFileName = "";

            if (avatarPreview && avatarPreview.startsWith('data:')) {
                const parts = avatarPreview.split(',');
                avatarBase64 = parts[1];
                const mimeMatch = parts[0].match(/:(.*?);/);
                const mime = mimeMatch ? mimeMatch[1] : 'image/png';
                avatarExt = "." + (mime.split('/')[1] || 'png');
                avatarFileName = `avatar_${Date.now()}${avatarExt}`;
            }

            const payload = {
                Token: "-2295521862261168",
                Details: {
                    parentId: parseInt(PARENT_ID || 34594),
                    userId: parseInt(LOGGED_USER_ID || 34594),
                    LeadId: parseInt(formData.LeadId || "0"),
                    LeadNo: String(formData.LeadNo || ""),
                    PersonName: formData.PersonName,
                    CompanyName: formData.CompanyName || "",
                    MobileNo: `${selectedCountries[0]?.dialCode || ""} ${String(formData.MobileNo[0] || "").replace(selectedCountries[0]?.dialCode || "", "").trim()}`.trim(),
                    MobileNo1: formData.MobileNo[1] ? `${selectedCountries[1]?.dialCode || ""} ${String(formData.MobileNo[1] || "").replace(selectedCountries[1]?.dialCode || "", "").trim()}`.trim() : "",
                    MobileNo2: formData.MobileNo[2] ? `${selectedCountries[2]?.dialCode || ""} ${String(formData.MobileNo[2] || "").replace(selectedCountries[2]?.dialCode || "", "").trim()}`.trim() : "",
                    EmailID: formData.EmailId[0] || "",
                    EmailID1: formData.EmailId[1] || "",
                    EmailID2: formData.EmailId[2] || "",
                    Country: formData.CountryId ? String(formData.CountryId) : "Select Country ",
                    State: String(formData.StateId || ""),
                    City: String(formData.CityId || ""),
                    PinCode: formData.Pincode || "0",
                    ResidentialAddress: formData.ResidentialAddress || "",
                    OfficialAddress: formData.OfficialAddress || "",
                    SourceName: String(formData.SourceId || "0"),
                    SourceId: 0,
                    MediumName: String(formData.MediumId || "0"),
                    MediumId: 0,
                    CampaignName: String(formData.CampaignId || "0"),
                    CampaignId: 0,
                    InitialRemarks: formData.InitialRemarks || "",
                    InitialRemarkId: 0,
                    WhereAddress: formData.WhereAddress || "",
                    WhereLatitude: formData.WhereLatitude || "",
                    WhereLongitude: formData.WhereLongitude || "",
                    LeadTags: formData.Tags || "",
                    PersonImage: "",
                    lstCustomField: customFields.map(f => {
                        const cleanKey = f.FieldName;
                        let val = formData[cleanKey] || "";
                        
                        // Format date to DD-MMM-YYYY as per user sample
                        if (getFieldType(cleanKey, f.DataType) === 'date' && val && val.includes('-')) {
                            const d = new Date(val);
                            if (!isNaN(d.getTime())) {
                                const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
                                val = `${String(d.getDate()).padStart(2, '0')}-${months[d.getMonth()]}-${d.getFullYear()}`;
                            }
                        }
                        
                        return {
                            FieldId: f.FieldId,
                            FieldValue: String(val)
                        };
                    })
                }
            };

            const response = await fetch(SaveEdit, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const data = await response.json();
            
            // Standard success check for these older WCF/ASMX services
            const result = data.d || data;
            const resLower = typeof result === 'string' ? result.toLowerCase() : "";
            const isSuccess = result.Status === 1 || result.Status === 200 || 
                             (result.Message && result.Message.toLowerCase().includes('success')) ||
                             resLower.includes('updated') ||
                             resLower.includes('success');

            if (isSuccess) {
                const displayMsg = typeof result === 'string' ? result : (result.Message || "Record Updated");
                showAlert('success', 'Success', displayMsg, () => {
                    if (onSave) onSave(formData);
                });
            } else {
                showAlert('error', 'Error', result.Message || "Failed to save details. Please check the data.");
            }
        } catch (error) {
            console.error('Error saving entity:', error);
            showAlert('error', 'Error', "An error occurred while saving. Please ensure you're connected to the network.");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="entity-edit-container">
            <style jsx>{`
                .form-checkbox { width: 18px; height: 18px; cursor: pointer; }
                .checkbox-wrapper { display: flex; align-items: center; gap: 8px; margin-top: 8px; }
            `}</style>
            <div className="entity-edit-header flex items-center justify-between !mb-0">
                <div className="flex items-center gap-3">
                    <button type="button" className="p-1.5 hover:bg-slate-200 rounded-full transition-colors" onClick={onCancel}>
                        <ArrowLeft size={20} />
                    </button>
                    <span className="text-lg font-semibold">Edit Entity Details</span>
                </div>
                <button type="button" className="p-1.5 hover:bg-slate-200 rounded-full transition-colors" onClick={onCancel}>
                    <X size={20} />
                </button>
            </div>
            <form onSubmit={handleSubmit} className="flex-1 flex flex-col overflow-hidden">
                <div className="entity-edit-layout-wrapper flex-1 flex overflow-hidden">
                    <div className="entity-edit-body flex-1 overflow-y-auto">
                    {isDetailsLoading && (
                        <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/70">
                            <Loader />
                        </div>
                    )}

                    {/* Basic Details Section */}
                    <div className="entity-edit-section">
                        <div className="entity-edit-sub-header">Basic Details</div>
                        <div className="p-5">
                            <div className="basic-details-layout">
                                <div className="avatar-section">
                                    <div className="avatar-container" onClick={handleAvatarClick}>
                                        {avatarPreview ? (
                                            <img src={avatarPreview} alt="Profile" className="avatar-image" />
                                        ) : (
                                            <div className="avatar-initials">
                                                {getInitials(formData.PersonName)}
                                            </div>
                                        )}
                                    </div>
                                    <div className="edit-avatar-btn" onClick={handleAvatarClick}>
                                        <Pencil size={14} />
                                    </div>
                                    <input 
                                        type="file" 
                                        ref={fileInputRef} 
                                        style={{ display: 'none' }} 
                                        accept="image/*" 
                                        onChange={handleFileChange} 
                                    />
                                </div>

                                <div className="fields-grid-2">
                                    <div className="form-group">
                                        <label className="form-label">Name <span className="required">*</span></label>
                                        <input 
                                            type="text" 
                                            name="PersonName" 
                                            className="form-input" 
                                            placeholder="Enter name"
                                            value={formData.PersonName}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Company</label>
                                        <input 
                                            type="text" 
                                            name="CompanyName" 
                                            className="form-input" 
                                            placeholder="Company name"
                                            value={formData.CompanyName}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                    {/* Multi-Contact Section (Independent Columns) */}
                                    <div className="contact-columns-layout">
                                        {/* Mobile Column */}
                                        <div className="contact-column">
                                            {(formData.MobileNo || ['']).map((mobile, idx) => (
                                                <div key={`mob-${idx}`} className="form-group">
                                                    <label className="form-label">
                                                        {idx === 0 ? 'Mobile' : `Mobile ${idx + 1}`} 
                                                        {idx === 0 && <span className="required">*</span>}
                                                    </label>
                                                    <div className="phone-input-container">
                                                        <div 
                                                            className="country-picker-wrapper" 
                                                            ref={idx === activeCountryPickerIdx ? countryPickerRef : null}
                                                        >
                                                            <div 
                                                                className="flag-section selectable" 
                                                                onClick={() => setActiveCountryPickerIdx(activeCountryPickerIdx === idx ? null : idx)}
                                                            >
                                                                <img src={selectedCountries[idx]?.flag || selectedCountries[0]?.flag} alt="flag" className="flag-icon" />
                                                                <span className="country-code">{selectedCountries[idx]?.dialCode || selectedCountries[0]?.dialCode}</span>
                                                            </div>

                                                            {activeCountryPickerIdx === idx && (
                                                                <div className="country-dropdown">
                                                                    <div className="country-search-wrapper">
                                                                        <input 
                                                                            type="text" 
                                                                            className="country-search-input" 
                                                                            placeholder="Search country..." 
                                                                            value={countrySearchTerm}
                                                                            onChange={(e) => setCountrySearchTerm(e.target.value)}
                                                                            autoFocus
                                                                            onClick={(e) => e.stopPropagation()}
                                                                        />
                                                                    </div>
                                                                    <div className="country-list">
                                                                        {allCountries
                                                                            .filter(c => 
                                                                                c.name.toLowerCase().includes(countrySearchTerm.toLowerCase()) || 
                                                                                c.dialCode.includes(countrySearchTerm)
                                                                            )
                                                                            .map((country, cIdx) => (
                                                                                <div 
                                                                                    key={`${country.code}-${cIdx}`} 
                                                                                    className="country-item"
                                                                                    onClick={() => {
                                                                                        const newCountries = [...selectedCountries];
                                                                                        newCountries[idx] = country;
                                                                                        setSelectedCountries(newCountries);
                                                                                        setActiveCountryPickerIdx(null);
                                                                                        setCountrySearchTerm('');
                                                                                    }}
                                                                                >
                                                                                    <img src={country.flag} alt="" className="country-item-flag" />
                                                                                    <span className="country-item-name">{country.name} ({country.dialCode})</span>
                                                                                </div>
                                                                            ))
                                                                        }
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                        <input 
                                                            type="tel" 
                                                            className="form-input phone-input" 
                                                            placeholder="Enter Mobile Number"
                                                            value={mobile || ''}
                                                            onChange={(e) => handleMultiInputChange('MobileNo', idx, e.target.value)}
                                                        />
                                                        <div className="action-box">
                                                            {idx === 0 ? (
                                                                <div className="action-icon-wrapper blue">
                                                                    <Pencil size={18} />
                                                                </div>
                                                            ) : (
                                                                <button type="button" className="action-icon-wrapper gray" onClick={() => removeMultiField('MobileNo', idx)}>
                                                                    <Minus size={18} />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                            {formData.MobileNo?.length < 3 && (
                                                <button type="button" className="add-contact-link" onClick={() => addMultiField('MobileNo')}>
                                                    <Plus size={14} /> Add another mobile
                                                </button>
                                            )}
                                        </div>

                                        {/* Email Column */}
                                        <div className="contact-column">
                                            {(formData.EmailId || ['']).map((email, idx) => (
                                                <div key={`email-${idx}`} className="form-group">
                                                    <label className="form-label">{idx === 0 ? 'Email' : `Email${idx}`}</label>
                                                    <div className="input-wrapper">
                                                        <input 
                                                            type="email" 
                                                            className="form-input" 
                                                            placeholder="Email"
                                                            value={email || ''}
                                                            onChange={(e) => handleMultiInputChange('EmailId', idx, e.target.value)}
                                                        />
                                                        <div className="action-box">
                                                            {idx === 0 ? (
                                                                // Primary email gets a hidden action box to maintain alignment
                                                                <div className="action-icon-wrapper hidden"></div>
                                                            ) : (
                                                                <button type="button" className="action-icon-wrapper gray" onClick={() => removeMultiField('EmailId', idx)}>
                                                                    <Minus size={18} />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                            {formData.EmailId?.length < 3 && (
                                                <button type="button" className="add-contact-link" onClick={() => addMultiField('EmailId')}>
                                                    <Plus size={14} /> Add another email
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="fields-grid-3" style={{marginTop: '20px'}}>
                                <div className="form-group">
                                    <label className="form-label">Country</label>
                                    <select 
                                        className="form-select" 
                                        name="CountryId"
                                        value={formData.CountryId}
                                        onChange={(e) => handleCountryChange(e.target.value)}
                                    >
                                        <option value="">Select Country</option>
                                        {Array.isArray(dropdowns.countries) && dropdowns.countries.map(c => (
                                            <option key={c.CountryID || c.Id || c.CountryId || c.Value} value={c.CountryID || c.Id || c.CountryId || c.Value}>
                                                {c.CountryName || c.Value || c.Text}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label className="form-label">State</label>
                                    <select 
                                        className="form-select"
                                        name="StateId"
                                        value={formData.StateId}
                                        onChange={(e) => handleStateChange(e.target.value)}
                                    >
                                        <option value="">Nothing selected</option>
                                        {Array.isArray(dropdowns.states) && dropdowns.states.map(s => (
                                            <option key={s.StateID || s.Id || s.StateId || s.Value} value={s.StateID || s.Id || s.StateId || s.Value}>
                                                {s.StateName || s.Value || s.Text}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label className="form-label">City</label>
                                    <select 
                                        className="form-select"
                                        name="CityId"
                                        value={formData.CityId}
                                        onChange={handleInputChange}
                                    >
                                        <option value="">Nothing selected</option>
                                        {Array.isArray(dropdowns.cities) && dropdowns.cities.map(c => (
                                            <option key={c.CityID || c.Id || c.CityId || c.Value} value={c.CityID || c.Id || c.CityId || c.Value}>
                                                {c.CityName || c.Value || c.Text}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Other Details Section */}
                    <div className="entity-edit-section">
                        <div className="entity-edit-sub-header">Other Details</div>
                        <div className="p-5">
                            <div className="fields-grid-3">
                                <div className="form-group">
                                    <label className="form-label">Medium</label>
                                    <select className="form-select" name="MediumId" value={formData.MediumId} onChange={handleInputChange}>
                                        <option value="">Select Value</option>
                                        {Array.isArray(dropdowns.mediums) && dropdowns.mediums.map(m => (
                                            <option key={m.SrcmedCampID || m.Value || m.id} value={m.SrcmedCampID || m.Value || m.id}>
                                                {m.SrcmedCampName || m.Text || m.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Campaign</label>
                                    <select className="form-select" name="CampaignId" value={formData.CampaignId} onChange={handleInputChange}>
                                        <option value="">Select Value</option>
                                        {Array.isArray(dropdowns.campaigns) && dropdowns.campaigns.map(c => (
                                            <option key={c.SrcmedCampID || c.Value || c.id} value={c.SrcmedCampID || c.Value || c.id}>
                                                {c.SrcmedCampName || c.Text || c.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Source <span className="required">*</span></label>
                                    <select className="form-select" name="SourceId" value={formData.SourceId} onChange={handleInputChange} required>
                                        <option value="">Select Value</option>
                                        {Array.isArray(dropdowns.sources) && dropdowns.sources.map(s => (
                                            <option key={s.SrcmedCampID || s.Value || s.id} value={s.SrcmedCampID || s.Value || s.id}>
                                                {s.SrcmedCampName || s.Text || s.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-group" style={{gridColumn: 'span 1.5'}}>
                                    <label className="form-label">Residential Address</label>
                                    <textarea name="ResidentialAddress" className="form-textarea" placeholder="Address" value={formData.ResidentialAddress} onChange={handleInputChange}></textarea>
                                </div>
                                <div className="form-group" style={{gridColumn: 'span 1.5'}}>
                                    <label className="form-label">Official Address</label>
                                    <textarea name="OfficialAddress" className="form-textarea" placeholder="Address" value={formData.OfficialAddress} onChange={handleInputChange}></textarea>
                                </div>

                                <div className="form-group fields-grid-full">
                                    <label className="form-label">Initial Remarks</label>
                                    <textarea name="InitialRemarks" className="form-textarea" placeholder="Initial Remarks" value={formData.InitialRemarks} onChange={handleInputChange}></textarea>
                                </div>

                                <div className="form-group">
                                    <label className="form-label">Pincode</label>
                                    <input type="number" name="Pincode" className="form-input" placeholder="0" value={formData.Pincode} onChange={handleInputChange} />
                                </div>

                                <div className="form-group fields-grid-full">
                                    <label className="form-label">Lead Tags</label>
                                    <div className="tag-input-container">
                                        <div className="tag-chips-wrapper">
                                            {(formData.Tags ? formData.Tags.split(',').map(t => t.trim()).filter(t => t) : []).map((tag, idx) => (
                                                <div key={idx} className="tag-chip">
                                                    <span>{tag}</span>
                                                    <button type="button" className="tag-remove-btn" onClick={() => removeTag(tag)}>
                                                        <X size={12} />
                                                    </button>
                                                </div>
                                            ))}
                                            <input 
                                                type="text" 
                                                className="tag-bare-input" 
                                                placeholder={formData.Tags ? "" : "Search tags"} 
                                                value={tagInputValue}
                                                onChange={(e) => setTagInputValue(e.target.value)}
                                                onKeyDown={handleTagKeyDown}
                                                onPaste={handleTagPaste}
                                            />
                                        </div>
                                    </div>
                                    <div className="tag-helper-box">
                                        Please press comma, tab, or enter key for generating the tag name, or you can also generate a tag by copy and paste with comma separated value.
                                    </div>
                                </div>
                            </div>

                            {/* Dynamic Other Details */}
                             <div className="fields-grid-3" style={{marginTop: '30px', borderTop: '1px solid #f1f5f9', paddingTop: '20px'}}>
                                {customFields.map((field, idx) => {
                                    const cleanKey = field.FieldName;
                                    const fieldType = getFieldType(cleanKey, field.DataType);
                                    const label = cleanKey.replace(/_/g, ' ');

                                    return (
                                        <div className="form-group" key={idx}>
                                            <label className="form-label">{label}</label>
                                            
                                             {fieldType === 'date' ? (
                                                <input 
                                                    type="date" 
                                                    name={cleanKey} 
                                                    className="form-input" 
                                                    value={formatDateForInput(formData[cleanKey])}
                                                    onChange={handleInputChange}
                                                />
                                            ) : fieldType === 'checkbox' ? (
                                                <div className="checkbox-wrapper">
                                                    <input 
                                                        type="checkbox" 
                                                        name={cleanKey} 
                                                        className="form-checkbox"
                                                        checked={!!formData[cleanKey]}
                                                        onChange={handleInputChange}
                                                        id={`cf-${idx}`}
                                                    />
                                                    <label htmlFor={`cf-${idx}`} className="checkbox-label">Enable {label}</label>
                                                </div>
                                            ) : fieldType === 'number' ? (
                                                <input 
                                                    type="number" 
                                                    name={cleanKey} 
                                                    className="form-input" 
                                                    placeholder={label}
                                                    value={formData[cleanKey] || ''}
                                                    onChange={handleInputChange}
                                                />
                                            ) : fieldType === 'radio' ? (
                                                <div className="radio-group">
                                                    {getRadioOptions(cleanKey).map(opt => (
                                                        <label key={opt} className="radio-option">
                                                            <input 
                                                                type="radio" 
                                                                name={cleanKey} 
                                                                value={opt} 
                                                                checked={formData[cleanKey] === opt}
                                                                onChange={handleInputChange}
                                                            />
                                                            <span className="radio-label">{opt}</span>
                                                        </label>
                                                    ))}
                                                </div>
                                            ) : fieldType === 'select' ? (
                                                <select 
                                                    name={cleanKey} 
                                                    className="form-select"
                                                    value={formData[cleanKey] || ''}
                                                    onChange={handleInputChange}
                                                >
                                                    <option value="">Select</option>
                                                    {getSelectOptions(cleanKey).map(opt => (
                                                        <option key={opt} value={opt}>{opt}</option>
                                                    ))}
                                                </select>
                                            ) : (
                                                <input 
                                                    type="text" 
                                                    name={cleanKey} 
                                                    className="form-input" 
                                                    placeholder={label}
                                                    value={formData[cleanKey] || ''}
                                                    onChange={handleInputChange}
                                                />
                                            )}
                                        </div>
                                    );
                                })}
                             </div>
                        </div>
                        </div>
                    </div>

                    {/* Action Operation Panel */}
                    {activeAction && (
                        <div className="entity-edit-action-panel">
                            <LeadActionOperation 
                                mode={activeAction}
                                entity={{
                                    enquiryId: formData.LeadId,
                                    leadId: formData.LeadId,
                                    id: formData.LeadId,
                                    PersonName: formData.PersonName,
                                    MobileNo: formData.MobileNo?.[0] || '',
                                    Email: formData.EmailId?.[0] || ''
                                }}
                                onBack={() => setActiveAction(null)}
                                onSuccess={() => setActiveAction(null)}
                            />
                        </div>
                    )}

                    {/* Right Side Sidebar Toolbar */}
                    <div className="entity-edit-sidebar">
                        {SIDEBAR_ACTIONS.map(action => (
                            <button 
                                key={action.id}
                                type="button"
                                className={`sidebar-action-btn ${activeAction === action.id ? 'active' : ''}`}
                                onClick={() => {
                                    if (action.id === 'quotation') {
                                        navigate(`/quotations?action=add&leadId=${formData.LeadId}`);
                                        if (onCancel) onCancel();
                                    } else if (action.id === 'invoice') {
                                        navigate(`/invoices?action=add&leadId=${formData.LeadId}`);
                                        if (onCancel) onCancel();
                                    } else if (action.id === 'tax-setting') {
                                        navigate(`/master-settings/tax-settings`);
                                        if (onCancel) onCancel();
                                    } else {
                                        setActiveAction(activeAction === action.id ? null : action.id);
                                    }
                                }}
                                title={action.label}
                                style={{ 
                                    '--active-color': action.color,
                                    color: action.color,
                                    background: activeAction === action.id ? `${action.color}15` : ''
                                }}
                            >
                                {action.icon}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="entity-edit-footer">
                    <button type="submit" className="save-btn" disabled={parentLoading || isDetailsLoading}>
                        {parentLoading ? 'Saving...' : 'Save'}
                    </button>
                </div>
            </form>

            <Alert 
                {...alertConfig} 
                onConfirm={handleAlertClose}
                onCancel={() => setAlertConfig(prev => ({ ...prev, show: false }))}
            />
        </div>
    );
};

export { EntityEdit };
export default EntityEdit;
